import { SALES_GRANULARITIES, type CompetitionDetail, type CompetitionSales, type SalesGranularity } from '@/api/competitionDetail'
import { EmptyState, formatCurrency, formatInteger, SegmentedControl, TrendChart, type FormatOptions } from '@/design'
import type { GlobalFilters } from '@/lib/filters'

import { bucketLabel } from '../format'
import { useSalesGranularity } from '../params'
import { Group, TabRead, Tile, TileGroup } from '../pieces'
import { useCompetitionDetail } from '../queries'

const GRANULARITY_LABELS: Record<SalesGranularity, string> = { day: 'Daily', week: 'Weekly' }

/**
 * Revenue and tickets over the period, a point per day or per seven days from its start
 * (the main route's `granularity`). The daily series is the page's own first read,
 * handed in, so opening this tab asks for nothing new; the weekly one is read when
 * Weekly is chosen.
 */
export function SalesTab({
  competitionId,
  daily,
  filters,
  money,
}: {
  competitionId: string
  /** The page's own read: the main route at `day`. */
  daily: CompetitionDetail
  filters: GlobalFilters
  money: FormatOptions
}) {
  const [granularity, setGranularity] = useSalesGranularity()
  const weekly = useCompetitionDetail(competitionId, filters, 'week', { enabled: granularity === 'week' })
  return (
    <div className="cd-stack">
      <div className="cd-controls">
        <SegmentedControl
          label="Sales by"
          options={SALES_GRANULARITIES.map((value) => ({ value, label: GRANULARITY_LABELS[value] }))}
          value={granularity}
          onChange={setGranularity}
        />
      </div>
      {granularity === 'day' ? (
        <SalesBody sales={daily.sales} money={{ ...money, currency: daily.currency }} />
      ) : (
        <TabRead read={weekly} loading="Loading weekly sales">
          {(detail) => <SalesBody sales={detail.sales} money={{ ...money, currency: detail.currency }} />}
        </TabRead>
      )}
    </div>
  )
}

function SalesBody({ sales, money }: { sales: CompetitionSales; money: FormatOptions }) {
  const { totals, series } = sales
  const per = sales.granularity === 'week' ? 'week' : 'day'
  return (
    <>
      <TileGroup title="In this period">
        <Tile label="Revenue" value={formatCurrency(totals.revenue, money)} note="Gross, before refunds" />
        <Tile label="Tickets sold" value={formatInteger(totals.tickets)} />
        <Tile label="Orders" value={formatInteger(totals.orders)} />
      </TileGroup>
      {totals.orders === 0 ? (
        <EmptyState
          variant="no-results"
          compact
          title="No sales in this period"
          description="No order with a ticket for this competition completed in this period. Try a wider date range."
        />
      ) : (
        <>
          <Group title={`Revenue per ${per}`} className="cd-chart">
            <TrendChart
              title={`Revenue per ${per}`}
              points={series.map((point) => ({ label: bucketLabel(point), current: point.revenue, previous: null }))}
              format="currency"
              formatOptions={money}
              currentLabel="Revenue"
            />
          </Group>
          <Group title={`Tickets sold per ${per}`} className="cd-chart">
            <TrendChart
              title={`Tickets sold per ${per}`}
              points={series.map((point) => ({ label: bucketLabel(point), current: point.tickets, previous: null }))}
              format="integer"
              currentLabel="Tickets"
            />
          </Group>
        </>
      )}
    </>
  )
}
